import * as mapboxgl from 'mapbox-gl';

// Bus-2.svg path pre-baked to canvas coordinates.
// Original viewBox: 88×75, center (37.5, 37.5). Transform applied offline:
//   new_x = x * 0.4 − 15,  new_y = y * 0.4 − 15
// Result: centered at origin, ~30×30 logical units. Tip points east (+x).
const VEHICLE_PATH = new Path2D(
    'M0 -15C3.7318 -15 7.1448 -13.6367 9.7695 -11.3816C9.9610 -11.2549 10.1439 -11.1149 10.3160 -10.9613L18.5824 -3.5848C20.7227 -1.6750 20.7210 1.6735 18.5789 3.5812L10.4934 10.7828C10.3581 10.9033 10.2154 11.0143 10.0680 11.1184C7.4061 13.5302 3.8749 15 0 15C-8.3143 15 -15 8.2843 -15 0C-15 -8.3143 -8.3143 -15 0 -15Z',
);

export class VehicleIcon {
    private readonly width = 96;
    private readonly height = 96;
    private data: Uint8ClampedArray = new Uint8ClampedArray(
        this.width * this.height * 4,
    );
    private ctx: CanvasRenderingContext2D;
    private readonly _map: any;
    private readonly _rgbColor: string;
    private readonly _animate: boolean;
    private _rendered = false;

    constructor(
        map: mapboxgl.Map,
        rgbColor: string,
        animate: boolean = false,
    ) {
        this._map = map;
        this._rgbColor = rgbColor;
        this._animate = animate;
    }

    public onAdd() {
        const canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        this.ctx = canvas.getContext('2d', {
            willReadFrequently: true,
        });
        this.ctx.scale(2, 2);
    }

    public render() {
        if (!this._animate && this._rendered) return false;
        // Logical drawing space is half the canvas (48×48); ctx.scale(2,2) handles the upscaling.
        const lw = this.width / 2;
        const lh = this.height / 2;
        const duration = 6000;
        const stopDuration = 4000;
        const t =
            performance.now() % duration < stopDuration
                ? 0
                : (performance.now() % (duration - stopDuration)) /
                  (duration - stopDuration);

        const radius = (lw / 2) * 0.3;
        const outerRadius = (lw / 2) * 0.7 * t + radius;
        const ctx = this.ctx;

        if (this._animate) {
            // Pulserande backgrund
            ctx.clearRect(0, 0, lw, lh);
            ctx.beginPath();
            ctx.arc(lw / 2, lh / 2, outerRadius, 0, Math.PI * 2);

            if (this._rgbColor.indexOf('rgb') >= 0) {
                const end = this._rgbColor.indexOf(')');
                const values = this._rgbColor.substring(4, end);
                ctx.fillStyle = `rgba(${values},${1 - t})`;
            }
            ctx.fill();
        }

        // VEHICLE_PATH is pre-baked to canvas coordinates (centered at origin).
        // Rotate around origin (canvas center 24,24) so the tip points in the
        // travel direction. The tip points east (+x); subtract 90° so bearing 0°
        // (north) renders the tip upward.
        ctx.save();
        ctx.translate(24, 24);
        ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = this._rgbColor;
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.lineWidth = 1;
        ctx.fill(VEHICLE_PATH);
        ctx.stroke(VEHICLE_PATH);
        ctx.restore();

        this.data = ctx.getImageData(0, 0, this.width, this.height).data;
        this._rendered = true;

        if (this._animate) {
            this._map.triggerRepaint();
        }
        // Always return true so Mapbox uploads this.data to the GPU texture.
        // The early-return at the top prevents re-drawing on subsequent frames
        // for non-animated icons, so render() is effectively called twice for
        // them: once to draw (returns true), once to stop (returns false).
        return true;
    }
}
